function parseChlFilename(filename) {
  const trimmed = filename.trim();
  const match = /^chl_(1d|8d)_(\d{8})\.csv(?:\.gz)?$/i.exec(trimmed);
  if (!match) {
    return null;
  }

  const period = match[1].toLowerCase();
  const dateText = match[2];
  const year = Number.parseInt(dateText.slice(0, 4), 10);
  const month = Number.parseInt(dateText.slice(4, 6), 10);
  const day = Number.parseInt(dateText.slice(6, 8), 10);
  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    filename: trimmed,
    period,
    date,
    dateText
  };
}

function nearestEntry(entries, targetDate) {
  if (!entries.length || !(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) {
    return null;
  }

  let best = null;
  let bestDiff = Number.POSITIVE_INFINITY;
  const targetMs = targetDate.getTime();

  entries.forEach((entry) => {
    const diff = Math.abs(entry.date.getTime() - targetMs);
    if (diff < bestDiff) {
      best = entry;
      bestDiff = diff;
      return;
    }

    if (diff === bestDiff && best && entry.date.getTime() < best.date.getTime()) {
      best = entry;
    }
  });

  return best;
}

export function selectNearestChlFile(chlDaysText, targetDate) {
  const lines = chlDaysText
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0 && !line.startsWith('#') && !line.startsWith('//'));

  const parsedEntries = lines
    .map(parseChlFilename)
    .filter(Boolean);

  const entries8d = parsedEntries.filter(entry => entry.period === '8d');
  const entries1d = parsedEntries.filter(entry => entry.period === '1d');

  const nearest8d = nearestEntry(entries8d, targetDate);
  if (nearest8d) {
    return nearest8d;
  }

  return nearestEntry(entries1d, targetDate);
}

// Returns all available Chl file entries of the given period ('1d' or '8d') from chl_days.txt, sorted by date ascending.
export function listChlEntries(chlDaysText, period) {
  const lines = chlDaysText
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0 && !line.startsWith('#') && !line.startsWith('//'));

  return lines
    .map(parseChlFilename)
    .filter((entry) => entry && entry.period === period)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

export function parseChlCsvRows(csvText) {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    return [];
  }

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const latIndex = headers.indexOf('latitude');
  const lonIndex = headers.indexOf('longitude');
  const chlIndex = headers.indexOf('chl');

  if (latIndex < 0 || lonIndex < 0 || chlIndex < 0) {
    return [];
  }

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = lines[i].split(',');
    const latitude = Number.parseFloat(parts[latIndex]);
    const longitude = Number.parseFloat(parts[lonIndex]);
    const chl = Number.parseFloat(parts[chlIndex]);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || !Number.isFinite(chl)) {
      continue;
    }

    rows.push({
      latitude,
      longitude,
      chl
    });
  }

  return rows;
}

export function computeChlRange(rows, minVisible = 0.01) {
  let max = Number.NEGATIVE_INFINITY;

  rows.forEach((row) => {
    if (row.chl > minVisible) {
      max = Math.max(max, row.chl);
    }
  });

  if (!Number.isFinite(max)) {
    return null;
  }

  return {
    minVisible,
    max
  };
}

export function normalizeChl(value, range) {
  if (!range || value <= range.minVisible) {
    return null;
  }

  const denominator = range.max - range.minVisible;
  if (denominator <= 0) {
    return 1;
  }

  // const normalized = (value - range.minVisible) / denominator;
  // return Math.max(0, Math.min(1, normalized));
  if (value <= 0.1) {
    return 0;
  } else if (value <= 0.25) {
    return 0.25;
  } else if (value <= .5) {
    return 0.5;
  } else if (value <= 1) {
    return 0.75;
  } else if (value <= 2) {
    return 0.85;  
  } else {
    return 1;
  }
}

export function formatChlLabel(selection, current_file_date) {
  if (!selection) {
    return 'Chl as of unknown';
  }

  const dateText = selection.dateText;
  const displayDate = `${dateText.slice(0, 4)}-${dateText.slice(4, 6)}-${dateText.slice(6, 8)}`;
  if (current_file_date && current_file_date !== "") {
    return `Chl as of ${displayDate} ${selection.period} | Currents as of ${current_file_date.slice(0, 4)}-${current_file_date.slice(4, 6)}-${current_file_date.slice(6, 8)}}`;
  } else {
    return `Chl as of ${displayDate} ${selection.period}`;
  }
}
